import { useRef, useEffect } from 'react'
import { VideoSource, VideoSourceProps } from './VideoSource'

interface VideoComponent {
  Source: React.FC<VideoSourceProps>
}

export interface VideoProps {
  autoPlay?: boolean
  controls?: boolean
  isMuted?: boolean
  id: string
  isPlaying?: boolean
  onEnded?: () => void
  onPlay?: () => void
  onMetadataLoaded?: (metadata: { duration: number }) => void
  setCurrentTime?: (newTime: number) => void
  preload?: 'auto' | 'metadata' | 'none'
  poster?: string
  videoLoop?: boolean
  videoPlayFromTime?: number
  volume?: number
}

export const Video: VideoComponent &
  React.FC<React.PropsWithChildren<VideoProps>> = ({
  autoPlay = false,
  controls = false,
  children,
  id,
  isMuted = false,
  isPlaying,
  onMetadataLoaded,
  onEnded,
  onPlay,
  preload = 'metadata',
  poster,
  setCurrentTime,
  videoLoop = false,
  videoPlayFromTime = 0,
  volume = 0.5,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hasEnded = (): void => onEnded && onEnded()
  const hasPlayed = (): void => onPlay && onPlay()

  function setMetadataLoaded(): void {
    if (videoRef.current !== null) {
      const { duration } = videoRef.current
      onMetadataLoaded?.({ duration })
    }
  }

  function onTimeUpdate(): void {
    if (videoRef.current !== null) {
      const video = videoRef.current
      setCurrentTime?.(video.currentTime)
    }
  }

  useEffect(() => {
    if (videoRef.current !== null) {
      const video = videoRef.current
      if (isPlaying && video.paused) video.play()
      if (!isPlaying && !video.paused) video.pause()
    }
  }, [isPlaying])

  useEffect(() => {
    if (videoRef.current !== null && videoPlayFromTime > 0) {
      const video = videoRef.current
      video.currentTime = videoPlayFromTime
    }
  }, [videoPlayFromTime])

  useEffect(() => {
    if (videoRef.current !== null) {
      const video = videoRef.current
      video.volume = volume
    }
  }, [volume])

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      autoPlay={autoPlay}
      className="lapidary-video"
      controls={controls}
      id={id}
      muted={isMuted}
      onEnded={hasEnded}
      onPlay={hasPlayed}
      onLoadedMetadata={setMetadataLoaded}
      poster={poster}
      preload={preload}
      onTimeUpdate={onTimeUpdate}
      ref={videoRef}
      loop={videoLoop}
    >
      {children}
    </video>
  )
}

Video.Source = VideoSource
